import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '../../api/client';
import UserAvatar, { getAvatarColor } from '../../components/UserAvatar';
import { UserStatusBadge, getUserStatus, USER_STATUS_CONFIG } from '../../components/ChangeStatusModal';
import { Tree, TreeNode } from 'react-organizational-chart';
import { X, User, Edit2, Eye, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = { super_admin: 'Super Admin', lider_proyecto: 'Líder', levantador: 'Levantador', cliente: 'Cliente' };
const ROLE_COLORS = { super_admin: '#7C3AED', lider_proyecto: '#1A56DB', levantador: '#15803D', cliente: '#D97706' };

function OrgNode({ user, onClick, isSelected }) {
  const statusCfg = USER_STATUS_CONFIG[getUserStatus(user)] || USER_STATUS_CONFIG.activo;
  return (
    <div
      onClick={() => onClick(user)}
      className={`inline-flex flex-col items-center bg-white border-2 rounded-sm shadow-sm p-3 cursor-pointer transition-all hover:shadow-md min-w-[140px] max-w-[160px] ${isSelected ? 'border-soem-primary bg-blue-50' : 'border-slate-200'}`}
    >
      <div className="relative mb-2">
        <UserAvatar user={user} size="md" />
        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${statusCfg.dot}`} />
      </div>
      <p className="text-xs font-bold text-soem-navy text-center leading-tight" style={{ maxWidth: '120px', wordBreak: 'break-word' }}>
        {user.full_name || user.name}
      </p>
      <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold mt-1 text-white"
        style={{ backgroundColor: ROLE_COLORS[user.role] || '#94a3b8', fontSize: '10px' }}>
        {ROLE_LABELS[user.role] || user.role}
      </span>
    </div>
  );
}

function buildTree(users) {
  const map = {};
  users.forEach(u => { map[u.id] = { ...u, children: [] }; });
  const roots = [];
  users.forEach(u => {
    if (u.direct_supervisor_id && map[u.direct_supervisor_id]) {
      map[u.direct_supervisor_id].children.push(map[u.id]);
    } else {
      roots.push(map[u.id]);
    }
  });
  return roots;
}

function renderNodes(nodes, selectedId, onSelect) {
  return nodes.map(node => (
    <TreeNode key={node.id} label={<OrgNode user={node} isSelected={node.id === selectedId} onClick={onSelect} />}>
      {node.children.length > 0 && renderNodes(node.children, selectedId, onSelect)}
    </TreeNode>
  ));
}

export default function OrgChart() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    usersAPI.list({ limit: 100 }).then(r => setUsers(r.data.items || [])).catch(() => toast.error('Error')).finally(() => setLoading(false));
  }, []);

  const trees = buildTree(users);
  const mainTree = trees.filter(u => ['super_admin', 'lider_proyecto'].includes(u.role));
  const orphans = trees.filter(u => !['super_admin', 'lider_proyecto'].includes(u.role));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="org-chart">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Organigrama del Equipo</h1>
          <p className="text-slate-500 text-sm mt-1">{users.length} miembros del equipo</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}
            className="soem-btn-secondary p-2"><ZoomOut size={16} /></button>
          <span className="text-sm text-slate-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}
            className="soem-btn-secondary p-2"><ZoomIn size={16} /></button>
          <button onClick={() => setZoom(1)} className="soem-btn-secondary px-3 py-2 text-xs">Centrar</button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Chart */}
        <div className="flex-1 soem-card overflow-auto p-6 min-h-96">
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', transition: 'transform 0.2s' }}>
            {mainTree.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <User size={48} className="mx-auto mb-3 text-slate-200" />
                <p>No hay usuarios con jerarquía definida.</p>
                <p className="text-xs mt-1">Asigna jefes directos para construir el organigrama.</p>
              </div>
            ) : (
              <Tree
                lineWidth="2px" lineColor="#e2e8f0" lineBorderRadius="4px"
                label={<div className="inline-block bg-soem-navy text-white text-xs font-bold px-4 py-1.5 rounded-sm">SOEM</div>}
              >
                {renderNodes(mainTree, selectedUser?.id, setSelectedUser)}
              </Tree>
            )}
          </div>

          {/* Orphan section */}
          {orphans.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="soem-label mb-3">Sin jefe asignado ({orphans.length})</p>
              <div className="flex flex-wrap gap-4">
                {orphans.map(u => (
                  <OrgNode key={u.id} user={u} isSelected={selectedUser?.id === u.id} onClick={setSelectedUser} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* User side panel */}
        {selectedUser && (
          <div className="w-64 flex-shrink-0 soem-card p-4 space-y-4 h-fit">
            <div className="flex items-center justify-between">
              <p className="soem-label">Perfil</p>
              <button onClick={() => setSelectedUser(null)} className="text-slate-400 hover:text-slate-600" aria-label="Cerrar"><X size={14} /></button>
            </div>
            <div className="text-center">
              <UserAvatar user={selectedUser} size="lg" className="mx-auto mb-2" />
              <p className="font-bold text-soem-navy">{selectedUser.full_name || selectedUser.name}</p>
              <p className="text-slate-400 text-xs">{selectedUser.email}</p>
              <div className="flex justify-center mt-2">
                <UserStatusBadge user={selectedUser} />
              </div>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">Rol</span>
                <span className="font-semibold text-soem-navy">{ROLE_LABELS[selectedUser.role]}</span>
              </div>
              {selectedUser.phone && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Teléfono</span>
                  <span className="font-semibold">{selectedUser.phone}</span>
                </div>
              )}
              {selectedUser.subordinates_count > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Subordinados</span>
                  <span className="font-semibold text-soem-navy">{selectedUser.subordinates_count}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate(`/dashboard/usuarios/${selectedUser.id}`)}
                className="soem-btn-primary flex-1 py-1.5 text-xs flex items-center justify-center gap-1">
                <Eye size={12} /> Ver perfil
              </button>
              <button onClick={() => navigate(`/dashboard/usuarios/${selectedUser.id}/editar`)}
                className="soem-btn-secondary px-3 py-1.5 text-xs">
                <Edit2 size={12} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        {Object.entries(ROLE_LABELS).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: ROLE_COLORS[k] }} />
            {v}
          </div>
        ))}
      </div>
    </div>
  );
}
